import {Component, State, Prop, Event, Element, EventEmitter, h, Listen} from '@stencil/core';
import Sortable from 'sortablejs';
import { v4 as uuidv4 } from 'uuid';
import {overrideSettings} from "../../utils/utils";

@Component({
  tag: 'fdi-sortable',
  styleUrl: 'fdi-sortable.scss',
  shadow: true,
})
export class FDISortable {

  @Element() private element: HTMLElement;

  @Prop() __settings: any;
  @Prop() __items: any = [];

  @State() items = new Map();
  @State() dataMap =  new Map();
  @State() data: any = [];
  @State() mainSortable: any = null;
  @State() innerSortables: any = {};
  @State() uuid;
  @State() settings: any = {
    nested: false,
    type: 'input',
    style: {
      sort: {
        width: 'auto',
      }
    },
    tooltip: {},
    tooltipTriggerHTML: '',
    addNestedItemText: '+',
    inputSettings: {},
    addItemText: 'Add Item',
    allowAddItem: true,
    removeItemText: '-',
    meta: {},
  };

  @Listen('fdi_wc_input_change')
  fdi_wc_input_change__handler( ev ){
    let data = ev.detail;
    if ( data.hasOwnProperty('meta') && data.meta.hasOwnProperty( 'outerUUID') ){
      if ( this.dataMap.has( data.meta.outerUUID ) && data.meta.hasOwnProperty( 'innerUUID' ) ){
        let innerData = this.dataMap.get( data.meta.outerUUID );
        if ( innerData.has( data.meta.innerUUID ) ){
          innerData.set( data.meta.innerUUID, data.newValue );
          this.dataMap.set( data.meta.outerUUID, innerData );
        }
      }
    }
    this.saveData( this.dataMap );
  }

  @Event({
    eventName: 'fdi_wc_sortable_update',
    bubbles: true,
    cancelable: true,
  }) dataUpdated: EventEmitter
  emitData(){
    console.log({
      emit: this.data,
    })
    this.dataUpdated.emit({
      data: this.data,
      meta: this.settings.meta,
    });
  }

  addItem( uuid = null ){
    if ( uuid && this.items.has( uuid ) ){
      let childUUID = uuidv4();
      this.items.get( uuid ).set( childUUID, '' );
      this.dataMap.get( uuid ).set( childUUID, '' );
      this.saveData( this.dataMap );
    }
    else{
      let uuid = uuidv4();
      let childUUID = uuidv4();
      this.items.set( uuid, ( new Map ).set( childUUID, '' ) );
      this.dataMap.set( uuid, ( new Map ).set( childUUID, '' ) );
      this.regenerateMainSortableList();
      this.saveData( this.dataMap );
    }
  }

  removeItem( uuid ){
    if ( uuid && this.items.has( uuid ) ){
      this.items.delete( uuid );
      this.dataMap.delete( uuid );
      this.regenerateMainSortableList();
      this.saveData( this.dataMap );
    }
  }

  componentWillLoad() {
    this.uuid = uuidv4();
    let settings = this.__settings;
    if ( typeof settings === 'string' ){
      settings = JSON.parse( settings );
    }
    this.settings = overrideSettings( this.settings, settings );

    let items = this.__items || [];
    if ( typeof items === 'string' ){
      items = JSON.parse( this.__items );
    }

    if ( Array.isArray( items ) ){
      let map = new Map();
      items.forEach( ( item ) => {
        let data = item;
        if ( Array.isArray( item ) ){
          data = new Map();
          item.forEach( ( innerItem ) => {
            data.set( uuidv4(), innerItem )
          });
        }
        map.set( uuidv4(), data );
        this.dataMap.set( uuidv4(), data );
        this.items.set( uuidv4(), data );
      });
      this.dataMap = new Map( map );
      this.items = new Map( map );
      this.saveData( map, false );
    }
  }

  saveData( data, emit = true ){
    let newData = [];
    data.forEach( ( map ) => {
      switch ( this.settings.type ){
        case 'html':
          newData.push( escape( map ) );
          break;
        case 'input':
        default:
          newData.push( Array.from( map.values() ) );
          break;
      }
    });
    this.data = JSON.parse( JSON.stringify( newData ) );
    if ( emit ) this.emitData();
  }

  innerItemClick( e, uuids ){
    if ( e.shiftKey ){
      this.items.get( uuids.outerUUID ).delete( uuids.innerUUID );
      this.dataMap.get( uuids.outerUUID ).delete( uuids.innerUUID );
      this.saveData( this.dataMap );
    }
  }

  createSortable( containerEl, level = 1 ){
    const opts = {
      ghostClass: 'sortableGhost',
      animation: 150,
      easing: "cubic-bezier(1, 0, 0, 1)",
      swapThreshold: .5,
    };
    let _vm = this;
    if ( level === 1 ){
      return Sortable.create( containerEl, {
        ...opts,
        ... {
          onSort: function(){
            let newOrder = _vm.mainSortable.toArray();
            let newItems = new Map();
            newOrder.forEach( ( uuid ) => {
              if ( _vm.dataMap.has( uuid) ){
                let innerData = _vm.dataMap.get( uuid );
                switch ( _vm.settings.type ){
                  case 'html':
                    newItems.set( uuid, innerData );
                    break;
                  case 'input':
                  default:
                    newItems.set( uuid, new Map( innerData ) );
                    break;
                }
              }
            });
            _vm.dataMap = new Map( newItems );
            _vm.saveData( _vm.dataMap );
          }
        }
      });
    }
    else {
      return Sortable.create( containerEl, {
        ...opts,
        ...{
          onSort: function( evt ){
            let innerSortableEl = evt.item;
            let parentUUID = innerSortableEl.dataset.idParent;
            if ( parentUUID ){
              let newOrder = _vm.innerSortables[parentUUID].toArray();
              if ( _vm.dataMap.has( parentUUID ) ){
                let parent = _vm.dataMap.get( parentUUID );
                let newItems = new Map();
                newOrder.forEach( ( uuid ) => {
                  if ( parent.has(uuid) ){
                    newItems.set( uuid, parent.get(uuid) );
                  }
                });
                _vm.dataMap.set( parentUUID, newItems );
                _vm.saveData( _vm.dataMap );
              }

            }
          }
        }
      });
    }
  }

  /**
   * This runs once, once the component is mounted.
   */
  componentDidLoad(){
    this.regenerateMainSortableList();
  }

  regenerateMainSortableList(){
    let items = this.element.shadowRoot.children;
    if ( this.mainSortable ) this.mainSortable.destroy();
    Array.from( items ).forEach( item => {
      if ( item.classList.contains( 'fdi-wc-sortable-container' ) ){
        Array.from( item.children ).forEach( child => {
          if ( child.classList.contains( 'fdi-wc-sortable-items' ) ){
            this.mainSortable = this.createSortable( child );
          }
        });
      }
    });
  }

  /**
   * This runs after every render.
   */
  componentDidRender(){
    if ( this.settings.nested ){
      let shadowRoot = this.element.shadowRoot;
      let nested = shadowRoot.querySelectorAll( '.innerItemContainer' );
      Array.from( nested ).forEach( ( el ) => {
        const uuid = el.getAttribute( 'data-id-parent' );
        if ( !this.innerSortables.hasOwnProperty( uuid ) ){
          this.innerSortables[ uuid ] = this.createSortable( el, 2 );
        }
      });
    }
  }

  render() {
    let list = Array.from( this.items ).map( ( data ) => {
      let outerUUID = data[0];
      let innerData = data[1];
      let returnContent = '';
      switch ( this.settings.type ){
        case 'html':
          returnContent = (
            <li style={ this.settings.style.sort } data-id={ outerUUID } innerHTML={ innerData }>
            </li>
          );
          break;
        case 'input':
        default:
          returnContent = (
            <li style={ this.settings.style.sort } data-id={outerUUID}>
              <div class={'itemMenu'}>
                <div onClick={ () => this.addItem( outerUUID ) }>{this.settings.addNestedItemText}</div>
                <div onClick={ () => this.removeItem( outerUUID ) }>{this.settings.removeItemText}</div>
              </div>
              <ul data-id-parent={outerUUID} class={'innerItemContainer'}>
                {Array.from( innerData ).map( ( innerData ) => {
                  let innerUUID = innerData[0];
                  let innerItem = innerData[1];
                  const inputSettings = overrideSettings({
                    fontSize: '15px',
                    placeholder: 'Write a sentence',
                    width: '',
                    margin: '1px',
                    growBasedOnInput: true,
                    meta: {
                      outerUUID,
                      innerUUID,
                    }
                  }, this.settings.inputSettings);
                  return (
                    <li data-id={ innerUUID } data-id-parent={ outerUUID } onClick={ ( e ) => this.innerItemClick( e, { innerUUID, outerUUID } ) }>
                      <fdi-text-input __value={ innerItem } __settings={ inputSettings } />
                    </li>
                  );
                })}
              </ul>
            </li>
          )
          break;
      }
      return returnContent;
    });
    const showTooltip = !!(Object.keys(this.settings.tooltip).length && this.settings.tooltipTriggerHTML);
    return (
      <div class={ 'fdi-wc-sortable-container' }>
        {
          showTooltip &&
          <div style={{display: 'block', cursor: 'pointer'}}>
            <span innerHTML={this.settings.tooltipTriggerHTML}></span>
            <fdi-tooltip __settings={this.settings.tooltip} />
          </div>
        }
        <ul id={ this.uuid } class={ 'fdi-wc-sortable-items' }>
          <div class={ 'fdi-wc-sortable-menu' }>
            {
              this.settings.allowAddItem &&
              <div style={this.settings.style.addItem} onClick={ () => this.addItem() }>{ this.settings.addItemText }</div>
            }
          </div>
          { list }
        </ul>
      </div>
    );
  }
}
